/*
 * EDITOR CONTAINER COMPONENT
 *
 * Routes to the appropriate editor component based on editorContent type.
 *
 * Features:
 * - Switch statement for different editor types (kanban, settings, etc.)
 * - Placeholder when no editor content is set
 * - Extensible for future editor types
 * - Dark mode support
 *
 * Implementation notes:
 * - Casts editorContent props to editor-specific props after null checks
 *
 * Recent changes:
 * - Relaxed props casting to avoid TS2352 conversion errors
 *
 * Supported Editor Types:
 * - 'kanban': KanbanEditor component for board settings
 * - 'settings': Future - general settings editor
 */

import { FC } from 'react';
import { useChatContext } from '../../context/ChatContext';
import { THEME_CLASSES } from '../../constants/themeConstants';
import KanbanEditor, { type KanbanEditorProps } from '../kanban/KanbanEditor';

interface EditorContainerProps {
  onClose: () => void;
}

const EditorContainer: FC<EditorContainerProps> = ({ onClose }) => {
  const { editorContent } = useChatContext();

  // No content - show placeholder
  if (!editorContent) {
    return (
      <div className="h-full flex flex-col items-center justify-center p-6">
        <div className={`p-4 rounded-full bg-gray-100 dark:bg-gray-700 mb-4`}>
          <svg className={`w-8 h-8 ${THEME_CLASSES.text.secondary}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </div>
        <p className={`text-sm ${THEME_CLASSES.text.secondary} text-center`}>
          No editor content loaded
        </p>
      </div>
    );
  }

  // Route to appropriate editor based on type
  switch (editorContent.type) {
    case 'kanban':
      if (!editorContent.props) {
        return (
          <div className="h-full flex items-center justify-center p-6">
            <p className={`text-sm ${THEME_CLASSES.text.secondary}`}>
              Missing kanban editor configuration
            </p>
          </div>
        );
      }
      return <KanbanEditor {...(editorContent.props as unknown as KanbanEditorProps)} onClose={onClose} />;

    case 'settings':
      // Future: Settings editor
      return (
        <div className="h-full flex items-center justify-center p-6">
          <p className={`text-sm ${THEME_CLASSES.text.secondary}`}>
            Settings editor coming soon
          </p>
        </div>
      );

    default:
      return (
        <div className="h-full flex items-center justify-center p-6">
          <p className={`text-sm ${THEME_CLASSES.text.secondary}`}>
            Unknown editor type: {editorContent.type}
          </p>
        </div>
      );
  }
};

export default EditorContainer;
